/**
 * Admin Layout — DWMS EMR
 * Collapsible sidebar + header. Encounter editor handles its own surface
 * inside the main content area.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Toolbar } from '@mui/material';
import Sidebar, { DRAWER_WIDTH_EXPANDED, DRAWER_WIDTH_COLLAPSED } from '../components/Layout/Sidebar';
import Header from '../components/Layout/Header';
import useSidebar from '../hooks/useSidebar';
import IdleTimeoutGate from '../components/IdleTimeoutGate';
import AuthorizedUseGate from '../components/AuthorizedUseGate';

const AdminLayout = ({ children }) => {
    const { isCollapsed, mobileOpen, toggleCollapse, toggleMobile, closeMobile } = useSidebar();
    const sidebarWidth = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;
    // Routes are mounted under "/:role" (see routing/index.js), so the nav
    // base path must follow the logged-in user's role — hardcoding "/admin"
    // sent non-admin users (e.g. nurse) to /admin/* links.
    const role = useSelector((state) => state.auth.userData?.role);
    const basePath = `/${role || 'admin'}`;

    return (
        <IdleTimeoutGate>
        <AuthorizedUseGate>
        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
            <Header onMenuClick={toggleMobile} isCollapsed={isCollapsed} basePath={basePath} />
            <Sidebar
                basePath={basePath}
                isCollapsed={isCollapsed}
                mobileOpen={mobileOpen}
                onToggleCollapse={toggleCollapse}
                onCloseMobile={closeMobile}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 2, sm: 3 },
                    width: { xs: '100%', lg: `calc(100% - ${sidebarWidth}px)` },
                    maxWidth: '100%',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    transition: 'all 200ms ease',
                    bgcolor: 'background.default',
                }}
            >
                <Toolbar sx={{ mb: { xs: 2, sm: 3 } }} className="main-toolbar" />
                {children}
            </Box>
        </Box>
        </AuthorizedUseGate>
        </IdleTimeoutGate>
    );
};

export default AdminLayout;
